import { useEffect, useState } from 'react';
import { api } from '../services/api';
import type { HelpContent } from '@vedjegy/shared';

interface HelpModalProps {
  onClose: () => void;
}

export default function HelpModal({ onClose }: HelpModalProps) {
  const [content, setContent] = useState<HelpContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.config.getHelp()
      .then(setContent)
      .finally(() => setLoading(false));

    // Close on Escape
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const hasAnyContact = content && (
    content.contactName || content.contactEmail ||
    content.contactPhone || content.contactExtra
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 'var(--radius-lg)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          padding: '32px 36px', width: 420, maxWidth: '90vw',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 14, right: 16,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 20, color: 'var(--muted-foreground)', lineHeight: 1,
          }}
          aria-label="Bezárás"
        >
          ×
        </button>

        <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>
          Segítség
        </h2>
        <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--muted-foreground)' }}>
          Kérdés esetén vegye fel a kapcsolatot az alábbi elérhetőségeken:
        </p>

        {loading ? (
          <p style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Betöltés...</p>
        ) : !hasAnyContact ? (
          <p style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
            Kapcsolatfelvételi információ még nem érhető el.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {content!.contactName && (
              <Row icon="👤" label="Kapcsolattartó" value={content!.contactName} />
            )}
            {content!.contactEmail && (
              <Row icon="✉️" label="E-mail" value={
                <a href={`mailto:${content!.contactEmail}`} style={{ color: 'var(--primary)' }}>
                  {content!.contactEmail}
                </a>
              } />
            )}
            {content!.contactPhone && (
              <Row icon="📞" label="Telefon" value={
                <a href={`tel:${content!.contactPhone}`} style={{ color: 'var(--primary)' }}>
                  {content!.contactPhone}
                </a>
              } />
            )}
            {content!.contactExtra && (
              <div style={{
                marginTop: 6, padding: '12px 14px',
                background: 'var(--muted)', borderRadius: 'var(--radius-sm)',
                fontSize: 13, color: 'var(--primary)', whiteSpace: 'pre-wrap',
              }}>
                {content!.contactExtra}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon, label, value }: { icon: string; label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <span style={{ fontSize: 18, lineHeight: 1.4, minWidth: 22 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: 14, color: 'var(--primary)' }}>{value}</div>
      </div>
    </div>
  );
}
